/**
 * BW20-H — recovery-driven meal recommendations.
 *
 * Lifesum recommends recipes by category. Haus recommends meals by today's
 * biological state — Recovery Score + scheduled workout + GLP-1 dose day —
 * and the recommendation is framed as the daily prescription, not a
 * suggestion. The pure function below produces a `DailyMealPlan` that the
 * banner on /log/meals/:date can render directly.
 *
 * Behaviors:
 *   - Red recovery: drop pre_workout, +15% dinner kcal, bias toward
 *     iron + complex carbs, attach a non-blocking pointer to /training
 *     suggesting the user move tomorrow's hard session.
 *   - Green recovery + scheduled workout: insert pre_workout (90 min before)
 *     and post_workout (within 60 min after), shift dinner later if
 *     post_workout is in the dinner window.
 *   - Amber recovery: same as Green but with caveats in the banner copy.
 *   - GLP-1 day-of-week 0-1: narrow each meal max by 15%.
 */

import {
  computeMealTypeTargets,
  type MealType,
  type MealTypeTargetMap,
  type RecoveryState,
  type TrainingSession,
  type DietPattern,
} from "./meal-targets";

export type DailyMealPlanArgs = {
  recoveryState: RecoveryState;
  trainingSession?: TrainingSession;
  glp1DoseDayOfWeek?: number;
  bodyMassKg: number;
  dailyKcalTarget: number;
  dietPattern: DietPattern;
};

export type DailyMealPlanPointer = {
  href: string;
  label: string;
};

export type DailyMealPlanBanner = {
  state: string;
  meaning: string;
  nextMove: string;
  // Optional non-blocking pointer (e.g., to /training to defer tomorrow's
  // hard session). Renders as a "tap to move it" link in the banner.
  pointer?: DailyMealPlanPointer;
};

export type DailyMealPlan = {
  recoveryState: RecoveryState;
  targets: MealTypeTargetMap;
  banner: DailyMealPlanBanner;
  // Bias hints the recipe surface can filter on. Empty arrays mean no bias.
  recipeBias: {
    dinner: string[];
    pre_workout: string[];
    post_workout: string[];
  };
};

function timeOf(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function preWorkoutTime(session: TrainingSession): Date {
  const t = new Date(session.starts_at);
  t.setMinutes(t.getMinutes() - 90);
  return t;
}

function postWorkoutTime(session: TrainingSession): Date {
  const t = new Date(session.starts_at);
  t.setMinutes(t.getMinutes() + session.duration_min + 30);
  return t;
}

export function dailyMealRecommendation(args: DailyMealPlanArgs): DailyMealPlan {
  const targets = computeMealTypeTargets({
    dailyKcalTarget: args.dailyKcalTarget,
    dietPattern: args.dietPattern,
    bodyMassKg: args.bodyMassKg,
    recoveryState: args.recoveryState,
    trainingSessionToday: args.trainingSession,
    glp1DoseDayOfWeek: args.glp1DoseDayOfWeek,
  });

  const recipeBias: DailyMealPlan["recipeBias"] = {
    dinner: [],
    pre_workout: [],
    post_workout: [],
  };

  let banner: DailyMealPlanBanner;

  if (args.recoveryState === "red") {
    recipeBias.dinner = ["high_iron", "complex_carbs", "anti_inflammatory"];
    banner = {
      state:
        "Recovery is Red today. Pre-Workout slot is suppressed and dinner is +15% kcal.",
      meaning:
        "Iron + complex carbs at dinner support the next 24 to 48 hours of recovery. Tomorrow's hard session is worth re-evaluating.",
      nextMove:
        "Tap to move tomorrow's interval session. No automatic change is made.",
      pointer: { href: "/health/training", label: "tap to move it" },
    };
  } else if (
    args.trainingSession &&
    args.trainingSession.intensity !== "low" &&
    targets.pre_workout &&
    targets.post_workout
  ) {
    const pre = preWorkoutTime(args.trainingSession);
    const post = postWorkoutTime(args.trainingSession);
    recipeBias.pre_workout = ["low_fiber", "fast_carbs"];
    recipeBias.post_workout = ["high_protein", "high_carb_recovery"];
    if (args.recoveryState === "amber") {
      banner = {
        state: `Recovery is Amber. ${args.trainingSession.intensity} session at ${timeOf(args.trainingSession.starts_at)}. Pre and post slots are inserted.`,
        meaning:
          "The session can run as planned, but extra carbs at the post-workout slot help close the recovery gap.",
        nextMove: `Pre-workout snack around ${timeOf(pre)}. Post-workout meal by ${timeOf(post)}.`,
      };
    } else {
      banner = {
        state: `Recovery is Green. ${args.trainingSession.intensity} session at ${timeOf(args.trainingSession.starts_at)}.`,
        meaning:
          "Pre and post slots support the session. Dinner is shifted later so the post-workout meal lands within 60 minutes.",
        nextMove: `Pre-workout snack around ${timeOf(pre)}. Post-workout meal by ${timeOf(post)}.`,
      };
    }
  } else {
    banner = {
      state: `Recovery is ${
        args.recoveryState[0].toUpperCase() + args.recoveryState.slice(1)
      }. No workout scheduled.`,
      meaning: "Default meal slots apply.",
      nextMove: "Stay the course on the per-meal ranges.",
    };
  }

  // GLP-1 caveat tacked on the next-move line for week-1.
  if (args.glp1DoseDayOfWeek != null && args.glp1DoseDayOfWeek <= 1) {
    banner = {
      ...banner,
      nextMove: `${banner.nextMove} Each meal max is narrowed by 15% for the GLP-1 dose-week-1 window.`,
    };
  }

  return {
    recoveryState: args.recoveryState,
    targets,
    banner,
    recipeBias,
  };
}

export function recoveryDinnerShare(args: {
  baselineDinnerKcal: number;
  recoveryState: RecoveryState;
}): number {
  if (args.recoveryState === "red") {
    return Math.round(args.baselineDinnerKcal * 1.15);
  }
  return args.baselineDinnerKcal;
}

export function recoveryAffectedSlots(targets: MealTypeTargetMap): MealType[] {
  return Object.keys(targets) as MealType[];
}
